import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { DonutDatum } from '@/api/types';
import { useSettings } from '@/api/settings/hooks';
import { useInView } from '@/lib/use-in-view';
import { cn } from '@/lib/utils';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Badge, type BadgeStatus } from '@/components/ui/badge';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
	type ProviderCard as ProviderCardData,
	type ProviderHealth,
} from '@/api/settings/mock';

/** Donut slice colors — the five brand/status chart tokens from index.css. */
const CHART_COLORS = [
	'var(--chart-1)',
	'var(--chart-2)',
	'var(--chart-3)',
	'var(--chart-4)',
	'var(--chart-5)',
] as const;

/** Domain provider health → shared Badge status color. Styling lives in the atom. */
const HEALTH_TO_BADGE: Record<ProviderHealth, BadgeStatus> = {
	operational: 'success',
	degraded: 'warning',
	down: 'danger',
};

const HEALTH_LABELS: Record<ProviderHealth, string> = {
	operational: 'Operational',
	degraded: 'Degraded',
	down: 'Down',
};

function HealthBadge({ health }: { health: ProviderHealth }) {
	return <Badge status={HEALTH_TO_BADGE[health]}>{HEALTH_LABELS[health]}</Badge>;
}

function UsageDonut({ data }: { data: DonutDatum[] }) {
	const { ref, inView } = useInView<HTMLDivElement>();
	const leader = data.reduce(
		(top, d) => (d.value > top.value ? d : top),
		data[0],
	);
	return (
		<Card className="p-5">
			<CardHeader className="space-y-1 p-0 pb-4">
				<CardTitle className="font-serif text-lg font-normal text-card-foreground">
					Usage split
				</CardTitle>
				<CardDescription>Share of send volume by provider.</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col items-center gap-8 p-0 sm:flex-row">
				<div ref={ref} className="relative h-52 w-52 shrink-0">
					<ResponsiveContainer width="100%" height="100%">
						<PieChart>
							<Pie
								data={data}
								dataKey="value"
								nameKey="name"
								innerRadius={62}
								outerRadius={90}
								paddingAngle={2}
								cornerRadius={4}
								stroke="none"
								isAnimationActive={inView}
							>
								{data.map((slice, i) => (
									<Cell
										key={slice.name}
										fill={CHART_COLORS[i % CHART_COLORS.length]}
									/>
								))}
							</Pie>
							<Tooltip
								cursor={false}
								formatter={(value: number, name: string) => [`${value}%`, name]}
								contentStyle={{
									background: 'var(--popover)',
									border: '1px solid var(--border)',
									borderRadius: '8px',
									color: 'var(--popover-foreground)',
									fontSize: '12px',
								}}
								itemStyle={{ color: 'var(--popover-foreground)' }}
							/>
						</PieChart>
					</ResponsiveContainer>
					{leader ? (
						<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
							<span className="font-serif text-2xl font-normal leading-none text-card-foreground">
								{leader.value}%
							</span>
							<span className="mt-1 text-xs text-muted-foreground">
								{leader.name}
							</span>
						</div>
					) : null}
				</div>
				<ul className="grid w-full grid-cols-2 gap-x-4 gap-y-3.5 sm:w-auto sm:flex-1">
					{data.map((slice, i) => (
						<li key={slice.name} className="flex items-start gap-2.5">
							<span
								className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-[3px]"
								style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
							/>
							<span>
								<span className="block text-[15px] font-semibold leading-tight text-card-foreground">
									{slice.value}%
								</span>
								<span className="block text-xs text-muted-foreground">
									{slice.name}
								</span>
							</span>
						</li>
					))}
				</ul>
			</CardContent>
		</Card>
	);
}

/**
 * Provider-status card. Config toggles (enabled + per-provider options) are
 * driven by local state only — no backend mutation exists yet (out of scope);
 * the E2E scenario just verifies the toggles respond to interaction.
 */
function ProviderCardView({ provider }: { provider: ProviderCardData }) {
	// Seed local state from the payload once; toggling flips it visibly in place.
	const [enabled, setEnabled] = useState(provider.enabled);
	const [toggles, setToggles] = useState<Record<string, boolean>>(() =>
		Object.fromEntries(provider.toggles.map((t) => [t.id, t.enabled])),
	);

	return (
		<Card className="flex flex-col gap-4 p-5">
			<CardHeader className="flex-row items-start justify-between gap-3 space-y-0 p-0">
				<div className="min-w-0">
					<CardTitle className="truncate font-serif text-lg font-normal text-card-foreground">
						{provider.name}
					</CardTitle>
					<p className="mt-0.5 font-mono text-xs text-muted-foreground">
						{provider.region} · {provider.usagePct}% of volume
					</p>
				</div>
				<HealthBadge health={provider.health} />
			</CardHeader>

			<CardContent className="flex flex-col gap-4 p-0">
				<div className="flex items-center justify-between border-t border-border pt-4">
					<label
						htmlFor={`enabled-${provider.id}`}
						className="text-sm font-medium text-foreground"
					>
						Enabled in routing pool
					</label>
					<Switch
						id={`enabled-${provider.id}`}
						checked={enabled}
						onCheckedChange={setEnabled}
					/>
				</div>

				<div
					className={cn(
						'flex flex-col gap-3 transition-opacity',
						enabled ? 'opacity-100' : 'opacity-50',
					)}
				>
					{provider.toggles.map((t) => (
						<div key={t.id} className="flex items-center justify-between gap-3">
							<label
								htmlFor={`${provider.id}-${t.id}`}
								className="text-sm text-muted-foreground"
							>
								{t.label}
							</label>
							<Switch
								id={`${provider.id}-${t.id}`}
								checked={toggles[t.id]}
								onCheckedChange={(v) =>
									setToggles((prev) => ({ ...prev, [t.id]: v }))
								}
							/>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

function SettingsSkeleton() {
	return (
		<div className="space-y-6">
			<Skeleton className="h-72 rounded-lg" />
			<div className="grid gap-4 md:grid-cols-2">
				{Array.from({ length: 4 }).map((_, i) => (
					<Skeleton key={i} className="h-56 rounded-lg" />
				))}
			</div>
		</div>
	);
}

function SettingsError({ onRetry }: { onRetry: () => void }) {
	return (
		<div className="flex flex-col items-center gap-4 py-16">
			<AlertCircle className="h-10 w-10 text-destructive" />
			<p className="text-muted-foreground">Failed to load provider settings.</p>
			<button
				type="button"
				onClick={onRetry}
				className="border border-border-strong px-4 py-2 text-sm text-foreground transition-colors hover:bg-accent"
			>
				Try again
			</button>
		</div>
	);
}

export default function SettingsPage() {
	const { data, isLoading, isError, refetch } = useSettings();

	return (
		<PageContainer>
			<PageHeader
				title="Settings"
				description="Provider configuration and send-volume routing."
			/>

			{isLoading ? (
				<SettingsSkeleton />
			) : isError || !data ? (
				<SettingsError onRetry={() => void refetch()} />
			) : (
				<div className="space-y-8">
					<UsageDonut data={data.usageSplit} />
					<section>
						<h2 className="mb-4 font-serif text-lg text-foreground">
							Providers
						</h2>
						<div className="grid gap-4 md:grid-cols-2">
							{data.providers.map((provider) => (
								<ProviderCardView key={provider.id} provider={provider} />
							))}
						</div>
					</section>
				</div>
			)}
		</PageContainer>
	);
}
